import { useState, useEffect } from 'react';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Upload, Sparkles, Loader2, FileImage } from 'lucide-react';
import { projectId } from '../utils/supabase/info';

import { bearerAuthHeader } from '../utils/apiHeaders';
interface VendorField {
  id: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'multiselect' | 'number' | 'boolean';
  required?: boolean;
  options?: string[];
  placeholder?: string;
  helpText?: string;
  category?: string;
}

interface VendorSpecificFormProps {
  vendor: 'jira' | 'servicenow' | 'zendesk' | 'ivanti-neurons' | 'bmc-helix' | 'freshservice' | 'custom';
  category: string;
  values: Record<string, any>;
  onChange: (values: Record<string, any>) => void;
  accessToken: string;
}

export function VendorSpecificForm({
  vendor,
  category,
  values,
  onChange,
  accessToken
}: VendorSpecificFormProps) {
  const [fields, setFields] = useState<VendorField[]>([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  
  // Load vendor-specific field schema
  useEffect(() => {
    loadFieldSchema();
  }, [vendor, category]);

  const loadFieldSchema = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/vendor-fields/${vendor}/${category}`,
        {
          headers: {
            ...bearerAuthHeader(),
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setFields(data.fields || []);
      }
    } catch (error) {
      console.error('Error loading vendor field schema:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value
    });
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setAnalyzing(true);
      setAnalysisResult(null);

      // Convert image to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result as string;

        // Call AI analysis endpoint
        const response = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/analyze-attachment`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              imageData: base64Data,
              category,
              vendor
            })
          }
        );

        if (response.ok) {
          const result = await response.json();
          setAnalysisResult(result);

          // Auto-populate fields with extracted data
          if (result.extractedData?.fields) {
            const newValues = { ...values };
            
            // Try to match extracted fields to form fields
            Object.entries(result.extractedData.fields).forEach(([key, value]) => {
              // Find matching field by label or id
              const matchingField = fields.find(f => 
                f.label.toLowerCase().includes(key.toLowerCase()) ||
                f.id.toLowerCase().includes(key.toLowerCase())
              );
              
              if (matchingField) {
                newValues[matchingField.id] = value;
              }
            });

            // If items array exists, try to populate textarea fields
            if (result.extractedData.items && result.extractedData.items.length > 0) {
              const firstTextareaField = fields.find(f => f.type === 'textarea');
              if (firstTextareaField && !newValues[firstTextareaField.id]) {
                newValues[firstTextareaField.id] = result.extractedData.items.join('\n');
              }
            }

            onChange(newValues);
          }
        } else {
          const error = await response.json();
          console.error('AI analysis error:', error);
          alert('Failed to analyze image: ' + (error.error || 'Unknown error'));
        }
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error analyzing image:', error);
      alert('Failed to analyze image');
    } finally {
      setAnalyzing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (fields.length === 0) {
    return (
      <Alert>
        <AlertDescription>
          No vendor-specific fields available for this configuration category.
        </AlertDescription>
      </Alert>
    );
  }

  const vendorName = {
    jira: 'Jira/JSM',
    servicenow: 'ServiceNow',
    zendesk: 'Zendesk',
    'ivanti-neurons': 'Ivanti Neurons',
    'bmc-helix': 'BMC Helix',
    freshservice: 'Freshservice',
    custom: 'Custom'
  }[vendor];

  return (
    <div className="space-y-4">
      {/* AI-Powered Upload Section */}
      <div className="border-2 border-dashed rounded-lg p-4 bg-muted/50">
        <div className="flex items-start gap-3">
          <Sparkles className="h-5 w-5 text-purple-600 mt-1 flex-shrink-0" />
          <div className="flex-1">
            <h4 className="font-medium mb-1">AI-Powered Form Fill</h4>
            <p className="text-sm text-muted-foreground mb-3">
              Upload a screenshot from {vendorName} or a workflow diagram, and AI will automatically extract and populate the fields below.
            </p>
            
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={analyzing}
                onClick={() => document.getElementById('image-upload')?.click()}
              >
                {analyzing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Screenshot
                  </>
                )}
              </Button>
              <input
                id="image-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
                disabled={analyzing}
              />
              
              {analysisResult && (
                <Badge variant="default" className="bg-green-600">
                  <FileImage className="h-3 w-3 mr-1" />
                  Data extracted
                </Badge>
              )}
            </div>

            {analysisResult && (
              <div className="mt-3 p-3 bg-background rounded border text-sm">
                <p className="font-medium mb-1">AI Analysis:</p>
                <p className="text-muted-foreground whitespace-pre-wrap line-clamp-3">
                  {analysisResult.analysis}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Vendor-Specific Fields */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-medium">{vendorName} Configuration</h4>
          <Badge variant="outline">{fields.length} fields</Badge>
        </div>
        {/* The old JiraConfigItemForm's tip (RESTORE_PLAN R1.7) */}
        <p className="text-xs text-muted-foreground" data-testid="vendor-form-tip">
          The more detail you capture now, the easier the build will be. Attach screenshots or workflow diagrams on the Files tab.
        </p>

        {fields.map((field) => (
          <div key={field.id} className="space-y-2">
            <Label htmlFor={field.id}>
              {field.label}
              {field.required && <span className="text-destructive ml-1">*</span>}
            </Label>

            {field.type === 'text' && (
              <Input
                id={field.id}
                value={values[field.id] || ''}
                onChange={(e) => handleFieldChange(field.id, e.target.value)}
                placeholder={field.placeholder}
                required={field.required}
              />
            )}

            {field.type === 'textarea' && (
              <Textarea
                id={field.id}
                value={values[field.id] || ''}
                onChange={(e) => handleFieldChange(field.id, e.target.value)}
                placeholder={field.placeholder}
                rows={4}
                className="font-mono text-sm"
                required={field.required}
              />
            )}

            {field.type === 'select' && field.options && (
              <Select
                value={values[field.id] || ''}
                onValueChange={(value) => handleFieldChange(field.id, value)}
              >
                <SelectTrigger id={field.id}>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {field.options.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {field.type === 'number' && (
              <Input
                id={field.id}
                type="number"
                value={values[field.id] || ''}
                onChange={(e) => handleFieldChange(field.id, e.target.value)}
                placeholder={field.placeholder}
                required={field.required}
              />
            )}

            {field.type === 'boolean' && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={field.id}
                  checked={values[field.id] || false}
                  onCheckedChange={(checked) => handleFieldChange(field.id, checked)}
                />
                <label
                  htmlFor={field.id}
                  className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {field.placeholder || 'Enable this option'}
                </label>
              </div>
            )}

            {field.helpText && (
              <p className="text-sm text-muted-foreground">{field.helpText}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}